import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Logo from '../assets/favicon.jpeg';
import { Eye, EyeOff, KeyRound, CheckCircle } from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

const ResetPassword: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(3);

  const API_BASE = getApiBase();

  useEffect(() => {
    setToken(new URLSearchParams(window.location.search).get('token'));
  }, []);

  useEffect(() => {
    if (!isSuccess) return;
    const interval = setInterval(() => setCountdown(c => c - 1), 1000);
    const timeout = setTimeout(() => window.close(), 3000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [isSuccess]);

  // Password strength
  const strength = (() => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    const labels = ['Very Weak', 'Weak', 'Medium', 'Strong', 'Very Strong'];
    const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#16a34a'];
    const i = Math.max(0, Math.min(score - 1, 4));
    return { score, label: labels[i], color: colors[i] };
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!token) { setMessage('Invalid or missing reset token.'); return; }
    if (password.length < 6) { setMessage('Password must be at least 6 characters.'); return; }
    if (password !== confirmPassword) { setMessage('Passwords do not match.'); return; }
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/auth/reset-password`, { token, password });
      setIsSuccess(true);
    } catch (err: any) {
      setMessage(err.response?.data?.msg || 'Reset link is invalid or expired.');
    } finally { setLoading(false); }
  };

  const inp: React.CSSProperties = { width: '100%', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px 14px', fontSize: '14px', color: '#111827', outline: 'none', boxSizing: 'border-box', background: '#fafafa' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 500, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f7f5', padding: 16, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      <div style={{ width: 400, maxWidth: '100%' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src={Logo} alt="JAC Logo" style={{ width: 64, height: 64, borderRadius: '16px', objectFit: 'cover', marginBottom: '14px' }} />
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 600, color: '#111827' }}>Scan Pay Go</h1>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#9ca3af' }}>Admin dashboard</p>
        </div>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: '20px', padding: '32px', border: '1px solid #f3f4f6', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>

          {isSuccess ? (
            /* ── Success state ─────────────────────────────────────────── */
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <CheckCircle size={28} color="#16a34a" />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 600, color: '#111827' }}>Password Reset!</h2>
              <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#6b7280', lineHeight: 1.6 }}>
                Your password has been updated successfully.
              </p>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#166534' }}>
                This tab will close in {countdown} second{countdown !== 1 ? 's' : ''}…
              </div>
            </div>
          ) : (
            /* ── Form ──────────────────────────────────────────────────── */
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: '#fff5f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <KeyRound size={20} color="#F1561D" />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Reset Password</h2>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#9ca3af' }}>Choose a strong new password</p>
                </div>
              </div>

              {message && (
                <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '10px', background: '#fff5f5', border: '1px solid #fecaca', fontSize: '13px', color: '#b91c1c' }}>
                  {message}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={labelStyle}>New Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      style={{ ...inp, paddingRight: '40px' }}
                      onFocus={e => (e.target.style.borderColor = '#F1561D')}
                      onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
                    />
                    <button type="button" onClick={() => setShowPass(s => !s)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 0 }}>
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {password && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ height: '4px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '4px', width: `${(strength.score / 5) * 100}%`, background: strength.color, transition: 'width 0.3s' }} />
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: '11px', color: strength.color }}>{strength.label}</p>
                    </div>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Confirm Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Repeat your password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      style={{ ...inp, paddingRight: '40px', borderColor: confirmPassword && password !== confirmPassword ? '#fca5a5' : '#e5e7eb' }}
                      onFocus={e => (e.target.style.borderColor = '#F1561D')}
                      onBlur={e => (e.target.style.borderColor = confirmPassword && password !== confirmPassword ? '#fca5a5' : '#e5e7eb')}
                    />
                    <button type="button" onClick={() => setShowConfirm(s => !s)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 0 }}>
                      {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#ef4444' }}>Passwords don't match</p>
                  )}
                </div>

                <button type="submit" disabled={loading}
                  style={{ marginTop: '4px', height: '44px', borderRadius: '10px', border: 'none', background: loading ? '#fdba99' : '#F1561D', color: '#fff', fontSize: '15px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.15s' }}>
                  {loading ? 'Resetting…' : 'Reset Password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
